import * as React from "react"
import {useRender} from "@base-ui/react/use-render"
import {clsx} from "clsx"
import styles from "./Label.module.css"

type LabelState = {
	disabled: boolean
}

interface LabelProps extends useRender.ComponentProps<"label", LabelState> {
	/**
	 * Whether the associated control is disabled. Only needed when the label sits
	 * *beside* its control (via htmlFor) and therefore can't detect it through
	 * :has(:disabled). Surfaced as a `data-disabled` attribute for styling.
	 */
	disabled?: boolean
}

/**
 * A label element, authored in the Base UI style — Base UI ships no standalone
 * Label (only Field.Label, coupled to Field). Encloses a control together with
 * its text, or sits beside one via `htmlFor`. Renders a `<label>` by default;
 * override the element with the `render` prop.
 */
const Label = React.forwardRef<HTMLLabelElement, LabelProps>(function Label(
	{className, render, disabled = false, ...elementProps},
	forwardedRef,
) {
	const state = React.useMemo<LabelState>(() => ({disabled}), [disabled])
	return useRender({
		render,
		ref: forwardedRef,
		state,
		defaultTagName: "label",
		props: {
			"data-slot": "label",
			className: clsx(styles.root, className),
			...elementProps,
		},
	})
})

export {Label}
